/* eslint-disable @typescript-eslint/prefer-literal-enum-member */
import type { AppointmentModel } from '@devexpress/dx-react-scheduler';
import { defineMessages } from 'react-intl';
import type { Frequency, Options } from 'rrule';
import { RRule, Weekday } from 'rrule';

import {
  BASIC_DAILY_COUNT,
  BASIC_MONTHLY_COUNT,
  BASIC_WEEKLY_COUNT,
  BASIC_YEARLY_COUNT,
  DAYS_IN_WEEK,
  DAYS_OF_WEEK_ARRAY,
  DAYS_OF_WEEK_DATES,
  DEFAULT_RULE_OBJECT,
  LAST_WEEK,
  RRULE_DAYS_OF_WEEK,
  RRULE_REPEAT_TYPES,
} from '@constants/date';

const messages = defineMessages({
  years: {
    id: 'routinesHelpers.years',
    defaultMessage: 'Years',
  },
  year: {
    id: 'routinesHelpers.year',
    defaultMessage: 'Year',
  },
  months: {
    id: 'routinesHelpers.months',
    defaultMessage: 'Months',
  },
  month: {
    id: 'routinesHelpers.month',
    defaultMessage: 'Month',
  },
  weeks: {
    id: 'routinesHelpers.weeks',
    defaultMessage: 'weeks',
  },
  week: {
    id: 'routinesHelpers.week',
    defaultMessage: 'week',
  },
  days: {
    id: 'routinesHelpers.days',
    defaultMessage: 'Days',
  },
  day: {
    id: 'routinesHelpers.day',
    defaultMessage: 'Day',
  },
  first: {
    id: 'routinesHelpers.first',
    defaultMessage: 'first',
  },
  second: {
    id: 'routinesHelpers.second',
    defaultMessage: 'second',
  },
  third: {
    id: 'routinesHelpers.third',
    defaultMessage: 'third',
  },
  fourth: {
    id: 'routinesHelpers.fourth',
    defaultMessage: 'fourth',
  },
  last: {
    id: 'routinesHelpers.last',
    defaultMessage: 'last',
  },
});

enum RecurrenceFrequency {
  Daily = RRULE_REPEAT_TYPES.DAILY,
  Weekly = RRULE_REPEAT_TYPES.WEEKLY,
  Monthly = RRULE_REPEAT_TYPES.MONTHLY,
  Yearly = RRULE_REPEAT_TYPES.YEARLY,
}

/**
 * Map a routine week number recurrence to friendly text
 * @param weekNo Week number that the recurrence repeats
 * @param formatter Formatter function
 * @returns A string representing the week number in plain text
 */
export const weekNumberToText = (
  weekNo: number,
  formatter: (msg: any) => string
) => {
  switch (weekNo) {
    case 1:
      return formatter(messages.first);
    case 2:
      return formatter(messages.second);
    case 3:
      return formatter(messages.third);
    case 4:
      return formatter(messages.fourth);
    case -1:
    default:
      return formatter(messages.last);
  }
};

/**
 * Map a routine recurrence's frequency to friendly text
 * @param freq Recurrence frequency
 * @param formatter Formatter function
 * @param multiple Indicates whether the returned text should be in plural form
 * @returns A string representing the frequency in plain text
 */
export const freqToText = (
  freq: Frequency,
  formatter: (msg: any) => string,
  multiple?: boolean
) => {
  switch (freq) {
    case RRULE_REPEAT_TYPES.YEARLY:
      return formatter(multiple ? messages.years : messages.year);
    case RRULE_REPEAT_TYPES.MONTHLY:
      return formatter(multiple ? messages.months : messages.month);
    case RRULE_REPEAT_TYPES.WEEKLY:
      return formatter(multiple ? messages.weeks : messages.week);
    case RRULE_REPEAT_TYPES.DAILY:
    default:
      return formatter(multiple ? messages.days : messages.day);
  }
};

/**
 * Get the default count for the recurrence frequency
 * @param frequency Recurrence frequency
 * @returns Default event count for the frequency
 */
export const getCountDependingOnRecurrenceType = (
  frequency: RecurrenceFrequency
) => {
  let count = 0;
  switch (frequency) {
    case RRULE_REPEAT_TYPES.YEARLY:
      count = BASIC_YEARLY_COUNT;
      break;
    case RRULE_REPEAT_TYPES.MONTHLY:
      count = BASIC_MONTHLY_COUNT;
      break;
    case RRULE_REPEAT_TYPES.WEEKLY:
      count = BASIC_WEEKLY_COUNT;
      break;
    case RRULE_REPEAT_TYPES.DAILY:
    default:
      count = BASIC_DAILY_COUNT;
      break;
  }
  return count;
};

/**
 * Change the frequency for a recurrence rule
 * @param rule Recurrence rule for a routine
 * @param freq New recurrence frequency
 * @param startDate Date that the routine starts on
 * @returns Updated recurrence rule
 */
export const changeRecurrenceFrequency = (
  rule: string | undefined,
  freq: RecurrenceFrequency,
  startDate: Date
) => {
  if (!rule) {
    if (freq === RRULE_REPEAT_TYPES.MONTHLY) {
      return new RRule({
        ...DEFAULT_RULE_OBJECT,
        freq: RRULE_REPEAT_TYPES.MONTHLY,
        bymonthday: [startDate.getDate()],
        count: getCountDependingOnRecurrenceType(freq),
      }).toString();
    }
    if (freq === RRULE_REPEAT_TYPES.YEARLY) {
      return new RRule({
        ...DEFAULT_RULE_OBJECT,
        freq: RRULE_REPEAT_TYPES.YEARLY,
        bymonthday: [startDate.getDate()],
        bymonth: startDate.getMonth() + 1,
        count: getCountDependingOnRecurrenceType(freq),
      }).toString();
    }
    if (freq === RRULE_REPEAT_TYPES.WEEKLY) {
      return new RRule({
        ...DEFAULT_RULE_OBJECT,
        freq: RRULE_REPEAT_TYPES.WEEKLY,
        count: getCountDependingOnRecurrenceType(freq),
      }).toString();
    }
    return new RRule({
      ...DEFAULT_RULE_OBJECT,
      freq: RRULE_REPEAT_TYPES.DAILY,
      count: getCountDependingOnRecurrenceType(freq),
    }).toString();
  }

  const options = RRule.parseString(rule);
  if (options.freq === Number(freq)) return rule;

  options.freq = Number(freq);
  options.count = getCountDependingOnRecurrenceType(freq);
  if (
    freq === RRULE_REPEAT_TYPES.MONTHLY ||
    freq === RRULE_REPEAT_TYPES.YEARLY
  ) {
    options.bymonthday = startDate.getDate();
  }
  if (freq === RRULE_REPEAT_TYPES.YEARLY) {
    options.bymonth = startDate.getMonth() + 1;
  }
  if (freq === RRULE_REPEAT_TYPES.DAILY || freq === RRULE_REPEAT_TYPES.WEEKLY) {
    options.bymonthday = undefined;
  }
  options.byweekday = undefined;
  const nextRule = new RRule(options);
  return nextRule.toString();
};

/**
 * Get the frequency for a recurrence rule
 * @param repeatType Repeat type for a recurrence rule
 * @returns Recurrence rule frequency
 */
export const getRRuleFrequency = (repeatType: string) => {
  switch (repeatType) {
    case 'YEARLY':
      return RRULE_REPEAT_TYPES.YEARLY;
    case 'MONTHLY':
      return RRULE_REPEAT_TYPES.MONTHLY;
    case 'WEEKLY':
      return RRULE_REPEAT_TYPES.WEEKLY;
    case 'DAILY':
    default:
      return RRULE_REPEAT_TYPES.DAILY;
  }
};

/**
 * Get the recurrence options for a recurrence rule
 * @param rule iCal formatted recurrence rule string
 * @returns The options for the recurrence rule
 */
export const getRecurrenceOptions = (rule: string) => {
  const options = rule ? RRule.parseString(rule) : {};
  return options;
};

/**
 * Change the options of a recurrence rule
 * @param options New options for a recurrence rule
 * @returns A recurrence rule containing the new options
 */
export const changeRecurrenceOptions = (
  options: Partial<AppointmentModel> & Partial<Options>
) => {
  return new RRule({ ...options }).toString();
};

/**
 * Get the recurrence display data for a radio group
 * @param recurrenceOptions Recurrence rule options
 * @param stateDayOfWeek The day of the week that that the routine recurs
 * @param stateWeekNumber The week number in a month that the routine recurs
 * @param stateDayNumber The day in a month that the routine recurs
 * @param firstOption Radio button value indicating that the routine recurs on a day-of-a-month
 * @param secondOption Radio button value indicating that the routine recurs on a day-of-the-week on a specific week number
 * @returns The display data for the radio group
 */
export const getRadioGroupDisplayData = (
  recurrenceOptions: Partial<Options>,
  stateDayOfWeek: number,
  stateWeekNumber: number,
  stateDayNumber: number,
  firstOption: string,
  secondOption: string
) => {
  if (!Object.keys(recurrenceOptions).length) {
    return {};
  }
  if (
    recurrenceOptions.bymonthday &&
    !Array.isArray(recurrenceOptions.bymonthday)
  ) {
    return {
      dayNumberTextField: recurrenceOptions.bymonthday,
      weekNumber: stateWeekNumber,
      dayOfWeek: stateDayOfWeek,
      radioGroupValue: firstOption,
    };
  }
  if (!recurrenceOptions.byweekday) {
    return {
      dayOfWeek: stateDayOfWeek,
      weekNumber: stateWeekNumber,
      radioGroupValue: secondOption,
      dayNumberTextField: stateDayNumber,
    };
  }
  if (
    recurrenceOptions.byweekday instanceof Array &&
    recurrenceOptions.byweekday[0] instanceof Weekday
  ) {
    const dayOfWeek =
      recurrenceOptions.byweekday[0].weekday < 6
        ? recurrenceOptions.byweekday[0].weekday + 1
        : 0;
    const weekNumber =
      recurrenceOptions.byweekday[0].n === -1 ||
      recurrenceOptions.byweekday[0].n === undefined
        ? LAST_WEEK
        : recurrenceOptions.byweekday[0].n - 1;

    return {
      dayOfWeek,
      weekNumber,
      radioGroupValue: secondOption,
      dayNumberTextField: stateDayNumber,
    };
  }
  return {};
};

/**
 * Change the day in a month that the routine recurs
 * @param nextStartDay Day that the routine should recur
 * @param options Existing recurrence options
 * @returns Updated recurrence options
 */
export const handleStartDateChange = (
  nextStartDay: number,
  options: Partial<Options>
) => {
  if (nextStartDay <= 31) {
    const nextOptions = { ...options, bymonthday: nextStartDay };
    return changeRecurrenceOptions(nextOptions);
  }
  return changeRecurrenceOptions(options);
};

/**
 * Change the day of the week or week number of a routine recurrence
 * @param weekNumber Week number that the routine should recur
 * @param dayOfWeek Day of the week that the routine should recur
 * @param options Existing recurrence options
 * @returns Updated recurrence options
 */
export const handleToDayOfWeekChange = (
  weekNumber: number,
  dayOfWeek: number,
  options: Partial<Options>
) => {
  const validDayOfWeek = dayOfWeek > 0 ? dayOfWeek - 1 : 6;
  const validWeekNumber = weekNumber === 4 ? -1 : weekNumber + 1;
  const rruleDayOfWeek = RRULE_DAYS_OF_WEEK[validDayOfWeek];

  const nextOptions: Partial<Options> = {
    ...options,
    byweekday: [rruleDayOfWeek.nth(validWeekNumber)],
    bymonthday: undefined,
  };

  const newOptions = changeRecurrenceOptions(nextOptions);
  return newOptions;
};

export const getCurrentMonth = (
  recurrenceOptions: Partial<Options>,
  appointmentData: AppointmentModel
) => {
  if (recurrenceOptions.bymonth) {
    return recurrenceOptions.bymonth instanceof Array
      ? recurrenceOptions.bymonth[0]
      : recurrenceOptions.bymonth;
  }
  return new Date(appointmentData.startDate).getMonth();
};

/**
 * Get an array of days of the week
 * @param firstDayOfWeek Number indicating which day the week starts on (Sunday (0) - Saturday (6))
 * @returns An array of numbers starting at `firstDayOfWeek`
 */
export const getDaysOfWeekArray = (firstDayOfWeek: number) => {
  const firstPart = DAYS_OF_WEEK_ARRAY.filter(
    (dayOfWeek) =>
      dayOfWeek >= firstDayOfWeek - 1 && dayOfWeek < DAYS_IN_WEEK - 1
  );
  const secondPart = DAYS_OF_WEEK_ARRAY.filter(
    (dayOfWeek) =>
      dayOfWeek < firstDayOfWeek - 1 || dayOfWeek >= DAYS_IN_WEEK - 1
  );
  return firstDayOfWeek === 0
    ? [...secondPart, ...firstPart]
    : [...firstPart, ...secondPart];
};

/**
 * Get an array of dates representing days of the week
 * @param firstDayOfWeek Number indicating which day the week starts on (0-6)
 * @returns An array of dates representing each day of the week, Sunday to Saturday, starting at `firstDayOfWeek`
 */
export const getDaysOfWeekDates = (firstDayOfWeek: number) => {
  const firstPart = DAYS_OF_WEEK_DATES.slice(
    firstDayOfWeek,
    DAYS_OF_WEEK_DATES.length
  );
  const secondPart = DAYS_OF_WEEK_DATES.slice(0, firstDayOfWeek);
  return [...firstPart, ...secondPart];
};

/**
 * Change which day of the week a routine recurs
 * @param options Existing recurrence rule options
 * @param currentWeekDay Weekday to add to the recurrence rule
 * @returns Updated recurrence rule options
 */
export const handleWeekDaysChange = (
  options: Partial<Options>,
  currentWeekDay: number
) => {
  const byWeekDay = options.byweekday ?? [];
  const index = (byWeekDay as Weekday[]).findIndex(
    ({ weekday }) => weekday === currentWeekDay
  );

  const isAdded = index === -1;

  if (isAdded) {
    (byWeekDay as Weekday[]).push(RRULE_DAYS_OF_WEEK[currentWeekDay]);
  } else if (index > -1) {
    (byWeekDay as Weekday[]).splice(index, 1);
  }
  if (byWeekDay === 0) return { ...options, byweekday: undefined };
  return { ...options, byweekday: byWeekDay };
};
